import React, { useState, useEffect } from 'react';
import userService from '../services/userService';
import { getRoleColor, getRoleLabel } from '../utils/statusColors';
import ConfirmModal from '../components/ConfirmModal';
import UserActivityModal from '../components/UserActivityModal';
import NotificationModal from '../components/NotificationModal';
import './Usuarios.css';

const Usuarios = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState('create');
  const [selectedUser, setSelectedUser] = useState(null);
  const [filters, setFilters] = useState({ query: '', role: '', active: '' });
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [showActivityModal, setShowActivityModal] = useState(false);
  const [userForActivity, setUserForActivity] = useState(null);
  const [notification, setNotification] = useState({ show: false, type: 'success', title: '', message: '' });

  useEffect(() => {
    loadUsers();
  }, [filters]);

  const loadUsers = async () => {
    setLoading(true);
    try {
      const data = await userService.getUsers(filters);
      setUsers(data);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateUser = () => {
    setModalMode('create');
    setSelectedUser(null);
    setShowModal(true);
  };

  const handleEditUser = (user) => {
    setModalMode('edit');
    setSelectedUser(user);
    setShowModal(true);
  };

  const handleChangePassword = (user) => {
    setModalMode('password');
    setSelectedUser(user);
    setShowModal(true);
  };

  const handleDeleteUser = (user) => {
    setUserToDelete(user);
    setShowConfirmModal(true);
  };

  const confirmDeleteUser = async () => {
    try {
      await userService.deleteUser(userToDelete.id);
      setShowConfirmModal(false);
      setUserToDelete(null);
      loadUsers(); // Recargar la lista
    } catch (error) {
      setNotification({
        show: true,
        type: 'error',
        title: 'Error al Eliminar Usuario',
        message: error.response?.data?.detail || 'Error al eliminar usuario'
      });
      setShowConfirmModal(false);
      setUserToDelete(null);
    }
  };

  const cancelDeleteUser = () => {
    setShowConfirmModal(false);
    setUserToDelete(null);
  };

  const handleViewActivity = (user) => {
    setUserForActivity(user);
    setShowActivityModal(true);
  };

  const handleCloseActivity = () => {
    setShowActivityModal(false);
    setUserForActivity(null);
  };

  const handleModalClose = () => {
    setShowModal(false);
    setSelectedUser(null);
    loadUsers();
  };

  return (
    <div className="usuarios-container" data-testid="usuarios-page">
      <div className="usuarios-header">
        <div>
          <h1>Gestión de Usuarios</h1>
          <p>Administra usuarios y sus permisos</p>
        </div>
      </div>

      {/* Filters */}
      <div className="filters-card">
        <div className="filters-grid-users">
          <div className="filter-input">
            <label>Búsqueda</label>
            <input
              type="text"
              placeholder="Buscar por nombre o email..."
              value={filters.query}
              onChange={(e) => setFilters(prev => ({ ...prev, query: e.target.value }))}
              data-testid="search-users-input"
            />
          </div>
          <div className="filter-input">
            <label>Rol</label>
            <select value={filters.role} onChange={(e) => setFilters(prev => ({ ...prev, role: e.target.value }))} data-testid="role-filter">
              <option value="">Todos</option>
              <option value="admin">Admin</option>
              <option value="auditor">Auditor</option>
              <option value="consulta">Consulta</option>
            </select>
          </div>
          <div className="filter-input">
            <label>Estado</label>
            <select value={filters.active} onChange={(e) => setFilters(prev => ({ ...prev, active: e.target.value }))} data-testid="active-filter">
              <option value="">Todos</option>
              <option value="true">Activos</option>
              <option value="false">Inactivos</option>
            </select>
          </div>
        </div>
        <div className="filter-actions">
          <button onClick={handleCreateUser} className="create-btn" data-testid="create-user-button">+ Crear Usuario</button>
        </div>
      </div>

      {/* Users Table */}
      <div className="table-card">
        {loading ? (
          <div className="loading-state">
            <div className="spinner"></div>
            <p>Cargando usuarios...</p>
          </div>
        ) : (
          <div className="table-wrapper">
            <table className="usuarios-table" data-testid="users-table">
              <thead>
                <tr>
                  <th>Nombre</th>
                  <th>Email</th>
                  <th>Rol</th>
                  <th>Estado</th>
                  <th>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {users.map((user) => (
                  <tr key={user.id} data-testid={`user-row-${user.id}`}>
                    <td>{user.name}</td>
                    <td>{user.email}</td>
                    <td>
                      <span className={`badge ${getRoleColor(user.role)}`}>
                        {getRoleLabel(user.role)}
                      </span>
                    </td>
                    <td>
                      <span className={`badge ${user.active ? 'badge-active' : 'badge-inactive'}`}>
                        {user.active ? 'Activo' : 'Inactivo'}
                      </span>
                    </td>
                    <td>
                      <div className="action-buttons">
                        <button onClick={() => handleViewActivity(user)} className="action-btn-info" data-testid={`view-activity-${user.id}`}>Actividad</button>
                        <button onClick={() => handleEditUser(user)} className="action-btn" data-testid={`edit-user-${user.id}`}>Editar</button>
                        <button onClick={() => handleChangePassword(user)} className="action-btn-secondary" data-testid={`change-password-${user.id}`}>Contraseña</button>
                        <button onClick={() => handleDeleteUser(user)} className="action-btn-danger" data-testid={`delete-user-${user.id}`}>Eliminar</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showModal && <UserModal mode={modalMode} user={selectedUser} onClose={handleModalClose} />}
      
      {/* Modal de Confirmación de Eliminación */}
      <ConfirmModal
        isOpen={showConfirmModal}
        title="Eliminar Usuario"
        message={`¿Estás seguro de eliminar al usuario "${userToDelete?.name}"? Esta acción no se puede deshacer.`}
        onConfirm={confirmDeleteUser}
        onCancel={cancelDeleteUser}
        confirmText="Eliminar Usuario"
        cancelText="Cancelar"
        type="danger"
      />

      {/* Modal de Actividad del Usuario */}
      {showActivityModal && userForActivity && (
        <UserActivityModal
          user={userForActivity}
          onClose={handleCloseActivity}
        />
      )}

      <NotificationModal
        isOpen={notification.show}
        type={notification.type}
        title={notification.title}
        message={notification.message}
        onClose={() => setNotification({ show: false, type: 'success', title: '', message: '' })}
      />
    </div>
  );
};

const UserModal = ({ mode, user, onClose }) => {
  const [formData, setFormData] = useState({
    name: user?.name || '',
    email: user?.email || '',
    password: '',
    role: user?.role || 'consulta',
    active: user?.active !== undefined ? user.active : true,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      if (mode === 'create') {
        await userService.createUser(formData);
      } else if (mode === 'edit') {
        const { email, password, ...updateData } = formData;
        await userService.updateUser(user.id, updateData);
      } else if (mode === 'password') {
        await userService.changePassword(user.id, formData.password);
      }
      onClose();
    } catch (err) {
      setError(err.response?.data?.detail || 'Error al procesar');
    } finally {
      setLoading(false);
    }
  };

  const getTitle = () => {
    if (mode === 'create') return 'Crear Nuevo Usuario';
    if (mode === 'edit') return 'Editar Usuario';
    return 'Cambiar Contraseña';
  };

  return (
    <div className="modal-overlay" data-testid="user-modal">
      <div className="modal-content-small">
        <div className="modal-header">
          <h2>{getTitle()}</h2>
          <button onClick={onClose} className="modal-close" data-testid="close-user-modal">×</button>
        </div>
        <form onSubmit={handleSubmit} className="modal-form">
          {error && <div className="modal-error">{error}</div>}

          {mode !== 'password' && (
            <>
              <div className="form-field">
                <label>Nombre *</label>
                <input type="text" required value={formData.name} onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))} data-testid="user-name-input" />
              </div>
              {mode === 'create' && (
                <>
                  <div className="form-field">
                    <label>Email *</label>
                    <input type="email" required value={formData.email} onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))} data-testid="user-email-input" />
                  </div>
                  <div className="form-field">
                    <label>Contraseña *</label>
                    <input type="password" required value={formData.password} onChange={(e) => setFormData(prev => ({ ...prev, password: e.target.value }))} data-testid="user-password-input" />
                  </div>
                </>
              )}
              <div className="form-field">
                <label>Rol *</label>
                <select required value={formData.role} onChange={(e) => setFormData(prev => ({ ...prev, role: e.target.value }))} data-testid="user-role-select">
                  <option value="admin">Admin</option>
                  <option value="auditor">Auditor</option>
                  <option value="consulta">Consulta</option>
                </select>
              </div>
              {mode === 'edit' && (
                <div className="checkbox-field">
                  <input type="checkbox" id="active" checked={formData.active} onChange={(e) => setFormData(prev => ({ ...prev, active: e.target.checked }))} data-testid="user-active-checkbox" />
                  <label htmlFor="active">Usuario Activo</label>
                </div>
              )}
            </>
          )}

          {mode === 'password' && (
            <div className="form-field">
              <label>Nueva Contraseña *</label>
              <input type="password" required value={formData.password} onChange={(e) => setFormData(prev => ({ ...prev, password: e.target.value }))} data-testid="new-password-input" />
            </div>
          )}

          <div className="modal-actions">
            <button type="button" onClick={onClose} className="btn-cancel" data-testid="cancel-user-modal">Cancelar</button>
            <button type="submit" disabled={loading} className="btn-submit" data-testid="submit-user-modal">
              {loading ? 'Guardando...' : 'Guardar'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default Usuarios;